import instanceV2 from "./instanceV2";

// Mirrors the swebapp "Digital Records" save (savePDOnlyCase): one POST /cases
// creates the patient + the PD case (case_type 1, channel WEB), then each file
// is uploaded to a presigned S3 URL under that case. `source: 'drive'` tags the
// patient so the app's My Patients card shows the SmileFy Drive chip.
//
// Backend envelope: wrap() → { data: { result, region } }; axios → res.data.data.result.

const CHANNEL = "WEB";
const CASE_TYPE_PATIENT_DOCUMENTATION = 1;

// Drive file (category) → app file_type / file_category.
function scanTypeForCategory(category) {
  if (category === "PreOp Mandible" || category === "PostOp Mandible") return "IOS_SCAN_LOWER";
  return "IOS_SCAN_UPPER"; // PreOp/PostOp Maxilla + any other model
}

function descriptorFor(f, index) {
  const blob = f && f.file;
  if (!blob || typeof blob.arrayBuffer !== "function") return null;
  const fileName = blob.name || `file_${index}`;

  if (f.isImage) {
    return {
      blob,
      fileName,
      fileType: f.category === "Full Smile" ? "FULL_SMILE" : "AI_CLINICAL_DOC_IMAGE",
      fileCategory: "AI_SMILE_DESIGN",
    };
  }
  if (f.is3D) {
    return { blob, fileName, fileType: scanTypeForCategory(f.category), fileCategory: "INTRA_ORAL" };
  }
  if (f.isCBCT) {
    return { blob, fileName, fileType: "CBCT", fileCategory: "DIAGNOSTICS" };
  }
  return { blob, fileName, fileType: "OTHER_FILE", fileCategory: "AI_SMILE_DESIGN" };
}

// Build the full upload list, including one CALIBRATION_DATA singleton if any
// file carries a calibration project (matches the app's per-case calibration).
function buildDescriptors(files) {
  const out = [];
  (files || []).forEach((f, i) => {
    const d = descriptorFor(f, i);
    if (d) out.push(d);
  });
  const cal = (files || []).find((f) => f && f.calibrationProject);
  if (cal) {
    out.push({
      blob: new Blob([JSON.stringify(cal.calibrationProject)], { type: "application/json" }),
      fileName: "calibration_data.json",
      fileType: "CALIBRATION_DATA",
      fileCategory: "AI_SMILE_DESIGN",
    });
  }
  return out;
}

export default class PatientCaseService {
  // POST /patient-service-v2/cases → creates patient + PD case.
  static async createPdCase({ patientName, notes }) {
    const res = await instanceV2.post("/patient-service-v2/cases", {
      channel: CHANNEL,
      file_name: "case-doc.zip",
      case_type: CASE_TYPE_PATIENT_DOCUMENTATION,
      patient_name: patientName,
      source: "drive",
      increment: 0,
      notes: notes || "",
      ver_notes: "",
    });
    const cf = res?.data?.data?.result?.CaseFile;
    if (!cf || !cf.CaseId) throw new Error("Case create failed (no CaseFile)");
    return { caseId: cf.CaseId, id: cf.Id || cf.CaseId, patientId: cf.PatientId };
  }

  // PUT /patient-service-v2/files/upload → presigned S3 PUT URL for one file.
  static async getFileUploadUrl({ caseId, id, fileName, fileType, fileCategory }) {
    const res = await instanceV2.put("/patient-service-v2/files/upload", {
      case_id: caseId,
      id,
      file_name: fileName,
      file_type: fileType,
      file_category: fileCategory || "",
    });
    const url = res?.data?.data?.result?.URL;
    if (!url) throw new Error("No file upload URL");
    return url;
  }

  // PUT /patient-service-v2/patients/upload → presigned S3 PUT URL for the thumbnail.
  static async getPatientImageUploadUrl(patientId, fileName) {
    const res = await instanceV2.put("/patient-service-v2/patients/upload", {
      patient_id: patientId,
      file_name: fileName,
    });
    const url = res?.data?.data?.result?.URL;
    if (!url) throw new Error("No patient image upload URL");
    return url;
  }

  // PUT the blob to the presigned URL. Strip a typed blob's Content-Type (the
  // backend signs the PUT without one) to avoid S3 SignatureDoesNotMatch — same
  // trick the app's uploadBlobToSignedUrl uses.
  static async uploadBlobToSignedUrl(signedUrl, blob) {
    const body = blob.type === "" ? blob : new Blob([await blob.arrayBuffer()]);
    const r = await fetch(signedUrl, { method: "PUT", body });
    if (!r.ok) throw new Error(`Upload failed: ${r.status} ${r.statusText}`);
  }

  // Full "save patient to the app" flow. Returns { patientId, caseId, uploaded }.
  static async savePatientToApp({ patientName, files, notes, onProgress }) {
    const { caseId, id, patientId } = await this.createPdCase({ patientName, notes });

    const descriptors = buildDescriptors(files);

    // Thumbnail: first image file → patient profile image.
    const firstImage = (files || []).find(
      (f) => f && f.isImage && f.file && typeof f.file.arrayBuffer === "function"
    );
    if (firstImage) {
      try {
        const url = await this.getPatientImageUploadUrl(patientId, "patientThumb.jpg");
        await this.uploadBlobToSignedUrl(url, firstImage.file);
      } catch (e) {
        console.error("thumbnail upload failed (non-fatal):", e);
      }
    }

    let uploaded = 0;
    for (let i = 0; i < descriptors.length; i++) {
      const d = descriptors[i];
      const url = await this.getFileUploadUrl({
        caseId,
        id,
        fileName: d.fileName,
        fileType: d.fileType,
        fileCategory: d.fileCategory,
      });
      await this.uploadBlobToSignedUrl(url, d.blob);
      uploaded++;
      onProgress?.(uploaded, descriptors.length);
    }

    return { patientId, caseId, uploaded };
  }
}
